import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import ReactApexChart from 'react-apexcharts';

import { Box, Flex, Text } from '@chakra-ui/react';

import { apiService } from 'services/ApiService';

import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import RangeSelect from './RangeSelect';

function SalesChart() {
  const [period, setPeriod] = useState('year');

  const { data, isLoading } = useQuery({
    queryKey: ['sales-chart', period],
    refetchOnWindowFocus: false,
    retry: false,
    queryFn: async () => {
      const response = await apiService.getStatsSales({
        range: period,
      });

      return response.data;
    },
  });

  const series = useMemo(
    () => [
      data?.FUEL_AND_RETAIL ?? 0,
      data?.FUEL_ONLY ?? 0,
      data?.RETAIL_ONLY ?? 0,
    ],
    [data],
  );

  const total = useMemo(() => series.reduce((sum, value) => sum + value, 0), [
    series,
  ]);

  const options = useMemo(
    () => ({
      chart: {
        type: 'donut',
        toolbar: {
          show: false,
        },
      },
      labels: ['Fuel & Retail', 'Fuel Only', 'Retail Only'],
      colors: ['#8C99ED', '#1870EC', '#F28C28'],
      stroke: {
        width: 3,
        colors: ['transparent'],
      },
      dataLabels: {
        enabled: true,
        formatter: (_, opts) => opts.w.config.series[opts.seriesIndex],
        style: {
          fontSize: '15px',
          fontWeight: 700,
          colors: ['#FFFFFF'],
        },
        dropShadow: {
          enabled: false,
        },
      },
      legend: {
        show: false,
        position: 'bottom',
        horizontalAlign: 'center',
        fontSize: '14px',
        fontFamily: 'Inter, Arial, sans-serif',
        fontWeight: 500,
        labels: {
          colors: '#9FABCB',
        },
        markers: {
          radius: 12,
        },
        itemMargin: {
          horizontal: 16,
          vertical: 6,
        },
      },
      plotOptions: {
        pie: {
          expandOnClick: false,
          offsetY: 6,
        },
      },
      tooltip: {
        theme: 'dark',
        y: {
          formatter: (value) => {
            const percent = total ? ((value / total) * 100).toFixed(1) : 0;
            return `${value} sales (${percent}%)`;
          },
        },
      },
    }),
    [total],
  );

  return (
    <Card minH={301} padding="24px 16px 12px 16px" w="100%">
      <Flex justifyContent="center" mb="12px">
        <Text variant="title-lg">Sales Distribution</Text>
      </Flex>

      <RangeSelect period={period} setPeriod={setPeriod} />

      <CardBody w="100%" padding="0">
        <Box w="100%">
          {isLoading ? (
            <Flex justifyContent="center" alignItems="center" height="322px">
              <Text>Loading chart data...</Text>
            </Flex>
          ) : (
            <ReactApexChart
              options={options}
              series={series}
              type="donut"
              height={315}
            />
          )}
        </Box>
      </CardBody>
    </Card>
  );
}

export default SalesChart;
